import { parseAmount } from "./convertNumbers";
import {
  formatBusinessTxAmountDisplay,
  formatMongoDecimalToZar,
  ZAR_RANDS_ZERO,
} from "./zarDisplayFormat";

export {
  formatBusinessTxAmountDisplay,
  formatMongoDecimalToZar,
} from "./zarDisplayFormat";

let fallbackTransactionRowIdSeq = 0;

export function makeFallbackBusinessTransactionRowId() {
  if (
    typeof crypto !== "undefined" &&
    typeof crypto.randomUUID === "function"
  ) {
    return `tx-${crypto.randomUUID()}`;
  }
  fallbackTransactionRowIdSeq += 1;
  return `tx-${Date.now()}-${fallbackTransactionRowIdSeq}`;
}

export function resolveBusinessTxStatusTone(statusLabel, fallbackTone) {
  if (fallbackTone === "complete" || fallbackTone === "pending") {
    return fallbackTone;
  }
  const u = (statusLabel || "").toUpperCase();
  if (u.includes("COMPLETE")) {
    return "complete";
  }
  if (u.includes("PENDING") || u.includes("AWAITING")) {
    return "pending";
  }
  return "other";
}

export function getBusinessWalletTransactionSortAmount(tx) {
  if (!tx || typeof tx !== "object") {
    return 0;
  }
  const ref = tx.transactionRef;
  const sub = String(tx.transactionSubtype || "").toLowerCase();
  const t = String(tx.transactionType || "");

  if (t === "UserTrade") {
    if (sub === "buy") {
      return parseAmount(
        ref?.incomingAmount?.$numberDecimal ??
          ref?.incomingAmount ??
          ref?.total?.$numberDecimal ??
          ref?.total
      );
    }
    if (sub === "sell") {
      return parseAmount(
        ref?.outgoingAmount?.$numberDecimal ??
          ref?.outgoingAmount ??
          ref?.incomingAmount?.$numberDecimal ??
          ref?.incomingAmount ??
          ref?.total?.$numberDecimal ??
          ref?.total
      );
    }
  }

  return parseAmount(
    ref?.amount?.$numberDecimal ??
      ref?.amount ??
      ref?.total?.$numberDecimal ??
      ref?.total ??
      tx.amount
  );
}

export function mapBusinessApiTxToRow(r) {
  if (!r || typeof r !== "object") {
    return null;
  }
  const id = String(
    r._id ??
      r.transactionRef?._id ??
      r.id ??
      makeFallbackBusinessTransactionRowId()
  );
  const t = r.transactionType != null ? String(r.transactionType) : "";
  const sub = String(r.transactionSubtype || "").toLowerCase();
  const activity = String(r.activity || "").toLowerCase();

  const ref =
    r.transactionRef != null && typeof r.transactionRef === "object"
      ? r.transactionRef
      : null;
  const pairLabel = ref?.pair
    ? String(ref.pair).toUpperCase().split("-").filter(Boolean).join(" · ")
    : "";

  let isOut =
    activity === "outgoing" ||
    /send|withdraw|out|fiat.*withdraw/i.test(t) ||
    sub.includes("send") ||
    sub === "sell";
  if (activity === "incoming") {
    isOut = false;
  }
  if (activity === "outgoing") {
    isOut = true;
  }

  const assetSym = String(r.asset || ref?.incomingAsset || "")
    .trim()
    .toUpperCase();

  let actionLabel = t || "Transaction";
  let actionLabelMobile = t || "Tx";
  if (t === "UserTrade") {
    if (sub === "buy") {
      actionLabel = assetSym ? `Buy ${assetSym}` : "Buy";
      actionLabelMobile = "Buy";
    } else if (sub === "sell") {
      actionLabel = assetSym ? `Sell ${assetSym}` : "Sell";
      actionLabelMobile = "Sell";
    } else {
      actionLabel = assetSym ? `Trade ${assetSym}` : "Trade";
      actionLabelMobile = "Trade";
    }
  }

  const rawPrimary =
    r.amount != null && String(r.amount).trim() !== ""
      ? String(r.amount)
      : [r.amount, r.asset].filter(Boolean).join(" ");
  const primaryAmount = formatBusinessTxAmountDisplay(rawPrimary);

  const secondaryLine = pairLabel || (ref?.pair ? String(ref.pair) : "");

  const createdAt = r.createdAt ? new Date(r.createdAt) : null;
  const timeStr = createdAt
    ? createdAt.toLocaleString("en-ZA", {
        dateStyle: "medium",
        timeStyle: "short",
      })
    : "";

  const statusStr = r.status != null ? String(r.status) : "—";

  const heroAmount =
    primaryAmount ||
    (ref?.total != null ? formatMongoDecimalToZar(ref.total) : "—");

  const serviceFeeStr = ref
    ? formatMongoDecimalToZar(ref.serviceFee)
    : ZAR_RANDS_ZERO;

  const detail = {
    heroZarAmount: heroAmount,
    listAmount: primaryAmount,
    transactionType:
      t === "UserTrade"
        ? sub === "buy"
          ? "Buy"
          : sub === "sell"
            ? "Sell"
            : "Trade"
        : t || "—",
    statusLabel: statusStr.toUpperCase(),
    statusTone: resolveBusinessTxStatusTone(statusStr.toUpperCase(), null),
    action:
      sub === "buy"
        ? "Buy"
        : sub === "sell"
          ? "Sell"
          : isOut
            ? "Send"
            : "Receive",
    transactionMethod: pairLabel || "—",
    transactionId: id,
    dateLine: createdAt
      ? createdAt.toLocaleDateString("en-ZA", {
          day: "numeric",
          month: "long",
          year: "numeric",
        })
      : "—",
    timeLine: createdAt
      ? createdAt.toLocaleTimeString("en-ZA", {
          timeStyle: "medium",
        })
      : "",
    serviceCharge: serviceFeeStr,
    totalCost: primaryAmount || heroAmount,
    showSplitPayment: false,
  };

  return {
    id,
    direction: isOut ? "sent" : "received",
    actionLabel,
    actionLabelMobile,
    time: timeStr,
    primaryAmount,
    secondaryAmount: secondaryLine,
    status: statusStr,
    detail,
  };
}
